import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  AI_QUOTA_CHANGED_EVENT,
  fetchMyAiQuota,
  type AiFeature,
  type AiQuota,
} from '../services/aiQuotaService';
import type { PlanAiFeatureKey } from '../constants/planFeatures';

/**
 * 이용권별 AI 권한·이번 달 잔여 크레딧(앱 전체 공유 캐시).
 * - 로그인 변경·AI 호출 후(AI_QUOTA_CHANGED_EVENT) 갱신.
 * - 서버가 최종 차단하므로 여기선 노출/안내만 담당.
 */
type Store = { quota: AiQuota | null; loaded: boolean; inflight: Promise<void> | null };
const store: Store = { quota: null, loaded: false, inflight: null };
const listeners = new Set<() => void>();
let authSubscribed = false;

function emit() {
  listeners.forEach((fn) => fn());
}

function reload(): Promise<void> {
  if (store.inflight) return store.inflight;
  store.inflight = fetchMyAiQuota()
    .then((q) => {
      store.quota = q;
    })
    .catch(() => {
      store.quota = null;
    })
    .finally(() => {
      store.loaded = true;
      store.inflight = null;
      emit();
    });
  return store.inflight;
}

/** 서버 응답 aiQuota(camelCase 숫자) 를 캐시에 합침. 권한은 그대로 둔다. */
function mergeServerQuota(detail: unknown) {
  if (!detail || typeof detail !== 'object' || !store.quota) {
    void reload();
    return;
  }
  const d = detail as Partial<AiQuota>;
  const next: AiQuota = { ...store.quota };
  if (typeof d.granted === 'number') next.granted = d.granted;
  if (typeof d.used === 'number') next.used = d.used;
  if (typeof d.remaining === 'number') next.remaining = d.remaining;
  if (typeof d.period === 'string') next.period = d.period;
  if (typeof d.resetAt === 'string') next.resetAt = d.resetAt;
  store.quota = next;
  emit();
}

function ensureGlobalSubscriptions() {
  if (authSubscribed || typeof window === 'undefined') return;
  authSubscribed = true;
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
      store.quota = null;
      store.loaded = false;
      void reload();
    }
  });
  window.addEventListener(AI_QUOTA_CHANGED_EVENT, (e) => mergeServerQuota((e as CustomEvent).detail));
}

export type UseAiAccess = {
  loading: boolean;
  quota: AiQuota | null;
  isAdmin: boolean;
  /** 이용권 권한(잔여와 무관). 로딩 중이면 false */
  can: (feature: AiFeature) => boolean;
  /** AI 세부 기능 권한(ai_curriculum_input·ai_evaluation·ai_record_feedback). 로딩 중이면 false */
  canPlan: (key: PlanAiFeatureKey) => boolean;
  /** 권한 있고 잔여가 남았는지(관리자는 항상 true) */
  hasCredits: boolean;
  refresh: () => Promise<void>;
};

export function useAiAccess(): UseAiAccess {
  const [, setTick] = useState(0);
  useEffect(() => {
    ensureGlobalSubscriptions();
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    if (!store.loaded && !store.inflight) void reload();
    return () => {
      listeners.delete(fn);
    };
  }, []);
  const quota = store.quota;
  const can = useCallback((feature: AiFeature) => Boolean(quota && quota.permissions[feature]), [quota]);
  const canPlan = useCallback((key: PlanAiFeatureKey) => Boolean(quota && quota.planFeatures?.[key]), [quota]);
  return {
    loading: !store.loaded,
    quota,
    isAdmin: Boolean(quota?.isAdmin),
    can,
    canPlan,
    hasCredits: Boolean(quota && (quota.unlimited || quota.remaining > 0)),
    refresh: reload,
  };
}

